import { useContext, useEffect, useState, useCallback } from 'react'
import {
  Stack,
  Text,
  DetailsList,
  IColumn,
  SelectionMode,
  DetailsListLayoutMode,
  IconButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
} from '@fluentui/react'
import { AppStateContext } from '../../state/AppProvider'
import { DashboardFilters, ConversationDetail } from '../../api/models'
import { getConversationsList, getFilterUsers } from '../../api/api'
import DateRangeFilter from '../../components/filters/DateRangeFilter'
import UserFilter from '../../components/filters/UserFilter'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ConversationModal from '../../components/conversations/ConversationModal'
import { formatDateTime } from '../../utils/formatDateTime'


const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
}

const PAGE_SIZE = 25

const Conversations = () => {
  const appStateContext = useContext(AppStateContext)
  const state = appStateContext?.state

  const getDefaultFilters = (): DashboardFilters => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    }
  }

  const [filters, setFilters] = useState<DashboardFilters>(state?.conversationsFilters || getDefaultFilters())
  const [items, setItems] = useState<ConversationDetail[]>(state?.conversationsData || [])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedConv, setSelectedConv] = useState<ConversationDetail | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortColumn, setSortColumn] = useState<string>('created_at')
  const [sortDescending, setSortDescending] = useState(true)

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDescending(!sortDescending)
    } else {
      setSortColumn(column)
      setSortDescending(true)
    }
    setCurrentPage(1)
  }

  const sortedItems = [...items].sort((a, b) => {
    const key = sortColumn as keyof ConversationDetail
    const aVal = a[key]
    const bVal = b[key]
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDescending ? bVal - aVal : aVal - bVal
    }
    const aStr = String(aVal ?? '').toLowerCase()
    const bStr = String(bVal ?? '').toLowerCase()
    return sortDescending ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr)
  })

  const dispatch = appStateContext?.dispatch

  const fetchData = useCallback(async (currentFilters: DashboardFilters) => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getConversationsList(currentFilters)
      setItems(data)
      setCurrentPage(1)
      if (dispatch) {
        dispatch({ type: 'SET_CONVERSATIONS_DATA', payload: { items: data, filters: currentFilters } })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations')
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!state?.conversationsData) {
      fetchData(filters)
    }
    if (dispatch && (!state?.users || state.users.length === 0)) {
      getFilterUsers().then((users) => dispatch({ type: 'SET_USERS', payload: users })).catch(() => {})
    }
  }, [])

  const columns: IColumn[] = [
    {
      key: 'created_at',
      name: 'Date',
      fieldName: 'created_at',
      minWidth: 140,
      maxWidth: 170,
      isSorted: sortColumn === 'created_at',
      isSortedDescending: sortColumn === 'created_at' && sortDescending,
      iconName: sortColumn !== 'created_at' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('created_at'),
      onRender: (item: ConversationDetail) => formatDateTime(item.created_at),
    },
    {
      key: 'user_name',
      name: 'User',
      fieldName: 'user_name',
      minWidth: 120,
      maxWidth: 180,
      isSorted: sortColumn === 'user_name',
      isSortedDescending: sortColumn === 'user_name' && sortDescending,
      iconName: sortColumn !== 'user_name' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('user_name'),
    },
    {
      key: 'title',
      name: 'Title',
      fieldName: 'title',
      minWidth: 300,
      isMultiline: true,
      isSorted: sortColumn === 'title',
      isSortedDescending: sortColumn === 'title' && sortDescending,
      iconName: sortColumn !== 'title' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('title'),
      onRender: (item: ConversationDetail) => item.title || '(untitled)',
    },
    {
      key: 'message_count',
      name: 'Questions',
      fieldName: 'message_count',
      minWidth: 90,
      maxWidth: 110,
      isSorted: sortColumn === 'message_count',
      isSortedDescending: sortColumn === 'message_count' && sortDescending,
      iconName: sortColumn !== 'message_count' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('message_count'),
      onRender: (item: ConversationDetail) => (
        <Text styles={{ root: { fontWeight: 600, color: '#2B3A67' } }}>{item.message_count}</Text>
      ),
    },
  ]

  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE)
  const startIdx = (currentPage - 1) * PAGE_SIZE
  const pagedItems = sortedItems.slice(startIdx, startIdx + PAGE_SIZE)

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        Conversations
      </Text>

      <Stack
        horizontal
        verticalAlign="end"
        tokens={{ childrenGap: 16 }}
        styles={{ root: { ...cardStyle.root, flexWrap: 'wrap' } }}
      >
        <DateRangeFilter
          startDate={filters.startDate || ''}
          endDate={filters.endDate || ''}
          onStartDateChange={(date) => setFilters({ ...filters, startDate: date })}
          onEndDateChange={(date) => setFilters({ ...filters, endDate: date })}
        />
        <UserFilter
          users={state?.users || []}
          selectedUserId={filters.userId}
          onChange={(userId) => setFilters({ ...filters, userId })}
        />
        <PrimaryButton text="Apply" onClick={() => fetchData(filters)} disabled={isLoading} />
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {isLoading && <LoadingSpinner label="Loading conversations..." />}

      {!isLoading && (
        <Stack styles={cardStyle} tokens={{ childrenGap: 12 }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
              Conversations
            </Text>
            <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>
              {items.length > PAGE_SIZE
                ? `Showing ${startIdx + 1}-${Math.min(startIdx + PAGE_SIZE, items.length)} of ${items.length}`
                : `${items.length} conversation${items.length !== 1 ? 's' : ''}`}
            </Text>
          </Stack>
          {items.length > 0 ? (
            <>
              <DetailsList
                items={pagedItems}
                columns={columns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                onRenderRow={(props, defaultRender) =>
                  props && defaultRender ? (
                    <div
                      onClick={() => setSelectedConv(props.item as ConversationDetail)}
                      style={{ cursor: 'pointer' }}
                    >
                      {defaultRender(props)}
                    </div>
                  ) : null
                }
                onShouldVirtualize={() => false}
              />
              <Stack
                horizontal
                horizontalAlign="center"
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
                styles={{ root: { paddingTop: 12 } }}
              >
                <IconButton
                  iconProps={{ iconName: 'ChevronLeft' }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  ariaLabel="Previous page"
                />
                <Text variant="medium">
                  Page {currentPage} of {totalPages}
                </Text>
                <IconButton
                  iconProps={{ iconName: 'ChevronRight' }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  ariaLabel="Next page"
                />
              </Stack>
            </>
          ) : (
            <Text styles={{ root: { color: '#a19f9d', textAlign: 'center', padding: 40 } }}>
              No conversations found for this period.
            </Text>
          )}
        </Stack>
      )}

      <ConversationModal
        content={selectedConv ? { kind: 'loaded', conversation: selectedConv } : null}
        onDismiss={() => setSelectedConv(null)}
      />
    </Stack>
  )
}

export default Conversations
